<script lang="ts">
import {defineComponent} from 'vue';
import {onNextFrame} from '../domutils.ts';

export default defineComponent({
  name: 'AllSpiceAccordion',
  props: {
    title: {type: String, required: true},
    ariaTitle: {type: String, required: true},
    open: {type: Boolean, default: false},
    showToggle: {type: Boolean, default: true},
  },
  data() {
    return {
      showPanel: this.open,
    };
  },
  methods: {
    togglePanel() {
      this.showPanel = !this.showPanel;
    },
    /**
     * Adjusts the height of an element to reveal its content with smooth animation.
     * @param {HTMLElement} element - The element to adjust the height of.
     * @returns {void}
     */
    enter(el: Element, _done: () => void) {
      // Set the height to auto to measure the content
      const element = el as HTMLElement;
      element.style.height = 'auto';
      const height = getComputedStyle(element).height;

      // Set the height back to 0 and animate to the measured height
      element.style.height = '0';

      onNextFrame(() => {
        element.style.height = height;
      });
      // TODO: Call done() after the animation completes to ensure the element is properly hidden
    },
    /**
     * Adjusts the height of an element to hide its content with smooth animation.
     * @param {HTMLElement} element - The element to adjust the height of.
     * @returns {void}
     */
    leave(el: Element, _done: () => void) {
      const element = el as HTMLElement;
      const height = getComputedStyle(element).height;

      element.style.height = height;

      requestAnimationFrame(() => {
        element.style.height = '0';
      });
      // TODO: Call done() after the animation completes to ensure the element is properly hidden
    },
    /**
     * Converts a string into a valid HTML ID by replacing spaces and non-alphanumeric characters with hyphens.
     * @param {string} inputString - The input string to be converted.
     * @returns {string} The converted HTML ID.
     */
    convertToHTMLId(inputString: string) {
      // Replace spaces and non-alphanumeric characters with hyphens
      return inputString
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
    },
  },
});
</script>
<template>
  <div class="panel">
    <div class="tw:text-center">
      <button
        v-if="showToggle"
        :id="`accordion-control-${convertToHTMLId(ariaTitle)}`"
        :aria-controls="`accordion-control-${convertToHTMLId(ariaTitle)}`"
        :aria-expanded="showPanel"
        class="ui button"
        type="button"
        @click.prevent="togglePanel"
      >
        <!-- Chevron Icon https://primer.style/foundations/icons/chevron-right-16/ -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          :class="{ rotated: showPanel }"
          class="svg"
          viewBox="0 0 16 16"
          width="16"
          height="16"
        >
          <path
            d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06Z"
          />
        </svg>
        {{ title }}
      </button>
    </div>
    <transition
      name="expand"
      @enter="enter"
      @leave="leave"
    >
      <div
        v-show="showPanel"
        :id="`content-${convertToHTMLId(ariaTitle)}`"
        :aria-hidden="!showPanel"
        class="content tw:mt-5"
        role="region"
        :aria-labelledby="`accordion-control-${convertToHTMLId(ariaTitle)}`"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>
<style scoped>
* {
  backface-visibility: hidden;
  perspective: 1000px;
  transform: translateZ(0);
  will-change: height;
}

svg {
  transition: transform 0.2s linear;
}
.rotated {
  transform: rotate(90deg);
}

.content {
  overflow: hidden;
  transition: height 0.3s ease-in-out;
}
</style>
